'use client';
import cn from 'classnames';
import './CompleteTheLookRecommendationGrid.style.scss';
import ProductInspirationCard, {
  ProductInspirationData
} from '@/components/common/ProductInspirationCard/ProductInspirationCard';
import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import LoadingGrid from './LoadingGrid';
import fallbackRecommendations from './FallbackRecommendations';
import { useCloset } from '@/components/hook/closet';
import { Closet, ClosetSlot } from '@/types/Closet';
import { ORDER_ITEM_STATUS_INTERNAL } from '@/types/Order';
import { Recommendation } from '@/types/Ella';
import analytics from '@/util/analytics';
import { customerHasPremierAddOn, getCustomerTier } from '@/util/customer';
import { ProductCardData } from '@/types/Product';
import { ParentComponent } from '@/types/Utils';

type CompleteTheLookRecommendationGridProps = {
  className?: string;
  numItemsToShow?: number;
  product?: ProductCardData;
};

function CompleteTheLookRecommendationGrid({
  className,
  numItemsToShow = 8,
  product
}: CompleteTheLookRecommendationGridProps) {
  const [loadingRecs, setLoadingRecs] = useState<boolean>(true);
  const [recommendations, setRecommendations] = useState<
    Pick<
      Recommendation,
      'id' | 'store' | 'name' | 'mainImage' | 'price' | 'cj_link'
    >[]
  >([]);

  const [rawRecommendations, setRawRecommendations] = useState<
    Recommendation[]
  >([]);

  const {
    value: { closet }
  } = useCloset();

  useEffect(() => {
    setLoadingRecs(true);
    const fetchRecommendations = async () => {
      try {
        // If we have a specific product (PDP view), use that
        // Otherwise get items from closet (landing page view)
        const products = product
          ? [product]
          : (closet?.slots || []).reduce((items: any[], slot: ClosetSlot) => {
              if (slot.currentItem) {
                items.push(slot.currentItem.productCardData);
              } else if (
                slot.returnedItem &&
                slot.returnedItem.status === ORDER_ITEM_STATUS_INTERNAL.RETURNED
              ) {
                items.push(slot.returnedItem.productCardData);
              }
              return items;
            }, []);

        const response = await fetch('/api/ella/recommendations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ products, numRecommendations: numItemsToShow })
        });

        const data = await response.json();

        if (data.success) {
          setRawRecommendations(data.recommendations);
          setRecommendations(data.recommendations);
        } else {
          console.error(`Failed to fetch recommendations: ${data.error}`);
          console.error(`Using fallback data`);
          setRecommendations(fallbackRecommendations);
        }
      } catch (error) {
        console.error(`Error fetching recommendations: ${error}`);
        console.error(`Using fallback data`);
        setRecommendations(fallbackRecommendations);
      } finally {
        setLoadingRecs(false);
      }
    };

    fetchRecommendations();
  }, [closet?.slots, product?.id, numItemsToShow]);

  let vivrelleProduct: ProductCardData[] | undefined;
  if (product) vivrelleProduct = [product];

  return (
    <div className={cn('CompleteTheLookRecGrid-container', className)}>
      {loadingRecs ? (
        <LoadingGrid numItemsToShow={numItemsToShow} />
      ) : (
        <div className={'CompleteTheLookRecGrid-item-grid'}>
          {recommendations.map((rec, index) => (
            <NextLink
              key={rec.id}
              href={rec.cj_link || ''}
              target='_blank'
              rel='noopener noreferrer'
            >
              <ProductInspirationCard
                key={rec.id}
                imageSrc={rec.mainImage}
                brandOrStore={rec.store.toUpperCase()}
                name={rec.name}
                price={Number(rec.price)}
                vertical={true}
                customerCloset={closet}
                /* TODO types Recommendation and ProductInspirationData overlap, we should consolidate */
                product={
                  rawRecommendations[index] as unknown as ProductInspirationData
                }
                vivrelleProduct={vivrelleProduct}
                parentComponent={ParentComponent.COMPLETE_THE_LOOK_GRID}
              />
            </NextLink>
          ))}
        </div>
      )}
    </div>
  );
}
export default CompleteTheLookRecommendationGrid;
